<template>
  <div class="entity-link-container">
    <!-- 背景装饰 -->
    <div class="leftSider">
      <img src="@assets/leftSider.png" alt="">
    </div>
    <div class="rightSider">
      <img src="@assets/rightSider.png" alt="">
    </div>

    <!-- Header -->
    <dp-header :title="'实体关联域名信息查询'"></dp-header>

    <!-- 搜索区域 -->
    <div class="search-section" :class="{ 'searched': hasSearched }">
      <div class="search-box">
        <el-autocomplete
          v-model="searchKeyword"
          :fetch-suggestions="fetchOrgSuggestions"
          placeholder="输入实体名称或关键词"
          class="search-input"
          @keyup.enter.native="handleSearch"
          @select="handleSearch"
          clearable
        >
          <el-button slot="append" icon="el-icon-search" @click="handleSearch">查询</el-button>
        </el-autocomplete>
      </div>

      <!-- 初始状态：显示已监控实体数量 -->
      <div v-if="!hasSearched" class="entity-count-tip">
        当前已监控的实体数量 <span class="count-number">{{ totalEntityCount }}</span> 个
      </div>

      <div v-if="!hasSearched" class="beian-footer">
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">鲁ICP备2026032922号-1</a>
        <span class="beian-divider">|</span>
        <a
          class="beian-police-link"
          href="https://beian.mps.gov.cn/#/query/webSearch?code=37140202001764"
          target="_blank"
          rel="noreferrer"
        >
          <img class="beian-police-logo" src="/beian-police.png" alt="公安备案图标">
          <span>鲁公网安备37140202001764号</span>
        </a>
      </div>
    </div>

    <!-- 查询结果区域 -->
    <div v-if="hasSearched" class="result-section">
      <!-- 实体基本信息（顶部跨宽）-->
      <div class="entity-info-card">
        <div class="entity-info-row">
          <div class="entity-name">实体规范名称：{{ entityInfo.实体规范名称 }}</div>
          <div class="domain-summary">
            域名资产整体情况：共有二级域名 <span class="highlight">{{ entityInfo.二级域名数量 }}</span> 个
          </div>
        </div>
      </div>

      <div class="result-layout">
        <!-- 主区：域名详情列表 -->
        <div class="result-main">
          <div class="domain-list-card">
            <div class="section-title">域名详情列表</div>
            <el-table
              :data="pagedDomainList"
              style="width: 100%"
              class="domain-list-table"
              @row-click="handleRowClick"
              :row-class-name="tableRowClassName"
            >
              <el-table-column prop="domain" label="域名" min-width="180"></el-table-column>
              <el-table-column label="归属机构" min-width="180">
                <template slot-scope="scope">
                  <span v-if="scope.row.companies === null" class="company-loading">查询中...</span>
                  <span v-else-if="scope.row.companies.length === 0" class="company-none">—</span>
                  <span v-else class="company-tags">
                    <el-tag
                      v-for="(c, i) in scope.row.companies"
                      :key="i"
                      size="mini"
                      type="info"
                      class="company-tag"
                    >{{ c }}</el-tag>
                  </span>
                </template>
              </el-table-column>
              <el-table-column prop="severe" label="严重配置风险警告" min-width="140">
                <template slot-scope="scope">
                  <span class="risk-count severe">{{ scope.row.severe }}条</span>
                </template>
              </el-table-column>
              <el-table-column prop="important" label="重要配置风险告警" min-width="140">
                <template slot-scope="scope">
                  <span class="risk-count important">{{ scope.row.important }}条</span>
                </template>
              </el-table-column>
              <el-table-column prop="general" label="一般配置提示" min-width="140">
                <template slot-scope="scope">
                  <span class="risk-count general">{{ scope.row.general }}条</span>
                </template>
              </el-table-column>
              <el-table-column prop="hijack" label="历史域名劫持事件" min-width="140">
                <template slot-scope="scope">
                  <span class="risk-count hijack">{{ scope.row.hijack }}次</span>
                </template>
              </el-table-column>
            </el-table>

            <!-- 分页 -->
            <el-pagination
              v-if="domainListData.length > 0"
              @current-change="handlePageChange"
              :current-page="currentPage"
              :page-size="pageSize"
              layout="total, prev, pager, next"
              :total="totalDomains"
              class="pagination"
            >
            </el-pagination>
          </div>
        </div>

        <!-- 侧边栏：风险统计 -->
        <aside class="result-sidebar">
          <div class="risk-stats-card">
            <div class="section-title">风险统计</div>
            <el-table
              :data="riskStatsData"
              style="width: 100%"
              class="risk-stats-table"
            >
              <el-table-column prop="riskSource" label="风险来源" min-width="100"></el-table-column>
              <el-table-column prop="riskLevel" label="风险等级" min-width="110">
                <template slot-scope="scope">
                  <span :class="getRiskClass(scope.row.riskLevel)">{{ scope.row.riskLevel }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="domainCount" label="二级域名数量" min-width="80"></el-table-column>
            </el-table>
          </div>
        </aside>
      </div>

      <div class="beian-footer">
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">鲁ICP备2026032922号-1</a>
        <span class="beian-divider">|</span>
        <a
          class="beian-police-link"
          href="https://beian.mps.gov.cn/#/query/webSearch?code=37140202001764"
          target="_blank"
          rel="noreferrer"
        >
          <img class="beian-police-logo" src="/beian-police.png" alt="公安备案图标">
          <span>鲁公网安备37140202001764号</span>
        </a>
      </div>
    </div>

    <!-- 域名风险详情弹窗 -->
    <el-dialog
      :title="`域名 ${selectedDomain} 的风险详情`"
      :visible.sync="dialogVisible"
      :modal="false"
      width="80%"
      class="risk-detail-dialog"
    >
      <div v-if="domainDetail" class="risk-detail-content">
        <!-- 严重配置风险警告 -->
        <div v-if="domainDetail.严重配置风险警告 && domainDetail.严重配置风险警告.length > 0" class="risk-category severe-category">
          <div class="category-title severe">严重配置风险警告（{{ domainDetail.严重配置风险警告.length }}条）</div>
          <div v-for="(risk, index) in domainDetail.严重配置风险警告" :key="'severe-' + index" class="risk-item">
            <div class="risk-type">{{ risk.风险类型 }}</div>
            <div class="risk-location">发生位置：{{ risk.发生位置 }}</div>
            <div class="risk-explanation">{{ risk.解释 }}</div>
          </div>
        </div>

        <!-- 重要配置风险告警 -->
        <div v-if="domainDetail.重要配置风险告警 && domainDetail.重要配置风险告警.length > 0" class="risk-category important-category">
          <div class="category-title important">重要配置风险告警（{{ domainDetail.重要配置风险告警.length }}条）</div>
          <div v-for="(risk, index) in domainDetail.重要配置风险告警" :key="'important-' + index" class="risk-item">
            <div class="risk-type">{{ risk.风险类型 }}</div>
            <div class="risk-location">发生位置：{{ risk.发生位置 }}</div>
            <div class="risk-explanation">{{ risk.解释 }}</div>
          </div>
        </div>

        <!-- 一般配置提示 -->
        <div v-if="domainDetail.一般配置风险提示 && domainDetail.一般配置风险提示.length > 0" class="risk-category general-category">
          <div class="category-title general">一般配置提示（{{ domainDetail.一般配置风险提示.length }}条）</div>
          <div v-for="(risk, index) in domainDetail.一般配置风险提示" :key="'general-' + index" class="risk-item">
            <div class="risk-type">{{ risk.风险类型 }}</div>
            <div class="risk-location">发生位置：{{ risk.发生位置 }}</div>
            <div class="risk-explanation">{{ risk.解释 }}</div>
          </div>
        </div>

        <!-- 域名劫持事件 -->
        <div v-if="domainDetail.域名劫持事件 && domainDetail.域名劫持事件.length > 0" class="risk-category hijack-category">
          <div class="category-title hijack">域名劫持事件（{{ domainDetail.域名劫持事件.length }}次）</div>
          <div v-for="(event, index) in domainDetail.域名劫持事件" :key="'hijack-' + index" class="hijack-item">
            <div class="hijack-time">发生时间：{{ event.发生时间 }}</div>
            <div class="hijack-ips">
              <span class="ip-label">正常IP:</span> {{ event.正常IP }}
              <span class="ip-label" style="margin-left: 20px;">异常IP:</span> {{ event.异常IP }}
            </div>
            <div class="hijack-analysis">
              <div><strong>HTTPS分析:</strong> {{ event.HTTPS分析 }}</div>
              <div><strong>DNS/TLS分析:</strong> {{ event['DNS/TLS分析'] }}</div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 查询加载遮罩 -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-content">
        <div class="loading-dots">
          <span></span><span></span><span></span>
        </div>
        <div class="loading-text">{{ loadingText }}</div>
      </div>
    </div>

    <!-- 底部导航 -->
    <navigation ref="nav" @navChange="handleNavChange"></navigation>

  </div>
</template>

<script>
import {
  getTotalEntity,
  getEntityInfo,
  getDomainAuthRisk,
  getDomainAuthRiskList,
  getDomainAuthRiskDetail,
  getDomainCompanyBatch
} from "@/service/dashboard";

export default {
  name: "EntityLink",
  data() {
    return {
      // 搜索相关
      searchKeyword: "",
      hasSearched: false,
      totalEntityCount: 0,

      // 实体信息
      entityInfo: {
        实体规范名称: "",
        二级域名数量: 0,
        子域名数量: 0
      },

      // 风险统计数据
      riskStatsData: [],

      // 域名列表数据
      domainListData: [],
      currentPage: 1,
      pageSize: 20,
      totalDomains: 0,

      // 弹窗相关
      dialogVisible: false,
      selectedDomain: "",
      domainDetail: null,

      // 加载状态
      isLoading: false,
      loadingText: "",
      loadingTimer: null,
      loadingSteps: [
        "Thinking...",
        "Crawling domain assets...",
        "Analyzing risk signals...",
        "Correlating threat data...",
        "Scanning DNS records...",
        "Evaluating security posture...",
        "Almost there..."
      ],
      loadingStepIndex: 0
    };
  },
  computed: {
    // 分页后的域名列表
    pagedDomainList() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;

      return this.domainListData.slice(start, end);
    }
  },
  mounted() {
    this.loadTotalEntity();

    // 检查是否有查询参数(从其他页面跳转过来)
    if (this.$route.query.keyword) {
      this.searchKeyword = this.$route.query.keyword;
      this.handleSearch();
    }
  },
  methods: {
    // 加载已监控实体总数
    async loadTotalEntity() {
      try {
        const res = await getTotalEntity();

        if (res.code === 200) {
          this.totalEntityCount = res.data;
        }
      } catch (error) {
        console.error("获取实体总数失败", error);
      }
    },

    // 后端已支持模糊查询，前端不再请求名称建议
    fetchOrgSuggestions(_, cb) {
      cb([]);
    },

    isEmptyBackendData(data) {
      if (!data) {
        return true;
      }
      if (Array.isArray(data)) {
        return data.length === 0;
      }
      if (typeof data !== "object") {
        return false;
      }
      return Object.values(data).every(value => {
        if (Array.isArray(value)) {
          return value.length === 0;
        }
        return value === undefined || value === null || value === "";
      });
    },

    hasDomainListData(data) {
      return Boolean(data && Array.isArray(data.域名) && data.域名.length > 0);
    },

    resetSearchResult() {
      this.hasSearched = false;
      this.entityInfo = { 实体规范名称: "", 二级域名数量: 0, 子域名数量: 0 };
      this.riskStatsData = [];
      this.domainListData = [];
      this.totalDomains = 0;
    },

    // 处理搜索
    async handleSearch() {
      if (!this.searchKeyword.trim()) {
        this.$message.warning("请输入实体名称或关键词");
        return;
      }
      // 重置分页到第一页
      this.currentPage = 1;
      this.isLoading = true;
      this.loadingStepIndex = 0;
      this.loadingText = this.loadingSteps[0];
      this.loadingTimer = setInterval(() => {
        this.loadingStepIndex = (this.loadingStepIndex + 1) % this.loadingSteps.length;
        this.loadingText = this.loadingSteps[this.loadingStepIndex];
      }, 800);

      try {
        // 并发请求三个接口
        const [entityInfoRes, riskStatsRes, domainListRes] = await Promise.all([
          getEntityInfo({ 实体名或关键词: this.searchKeyword }),
          getDomainAuthRisk({ 实体名或关键词: this.searchKeyword }),
          getDomainAuthRiskList({ 实体名或关键词: this.searchKeyword })
        ]);

        if (
          !(entityInfoRes.code === 200)
          || this.isEmptyBackendData(entityInfoRes.data)
          || !(domainListRes.code === 200)
          || !this.hasDomainListData(domainListRes.data)
        ) {
          this.resetSearchResult();
          this.$message.warning("该机构不在监测范围内");
          return;
        }

        if (entityInfoRes.code === 200) {
          this.entityInfo = entityInfoRes.data;
          this.hasSearched = true;
        }

        if (riskStatsRes.code === 200 && riskStatsRes.data) {
          // 转换风险统计数据格式
          const data = riskStatsRes.data;

          this.riskStatsData = (data.风险来源 || []).map((source, index) => ({
            riskSource: source,
            riskLevel: this.formatRiskLevelLabel(data.风险等级[index]),
            domainCount: data.二级域名数量[index],
            subdomainCount: data.子域名数量[index]
          }));
        }

        if (domainListRes.code === 200 && domainListRes.data) {
          // 转换域名列表数据格式
          const data = domainListRes.data;

          this.domainListData = (data.域名 || []).map((domain, index) => ({
            domain: domain,
            severe: data.严重配置风险警告[index],
            important: data.重要配置风险告警[index],
            general: data.一般配置风险提示[index],
            hijack: data.历史域名劫持事件[index],
            companies: null
          }));
          this.totalDomains = this.domainListData.length;

          // 批量查询所有域名的归属机构，超过1000个时分块并发
          const allDomains = this.domainListData.map(r => r.domain);
          const chunkSize = 1000;
          const chunks = [];

          for (let i = 0; i < allDomains.length; i += chunkSize) {
            chunks.push(allDomains.slice(i, i + chunkSize));
          }
          Promise.all(chunks.map(chunk => getDomainCompanyBatch(chunk))).then(responses => {
            const merged = {};

            responses.forEach(res => {
              if (res && res.result) {
                Object.assign(merged, res.result);
              }
            });
            this.domainListData.forEach((row, idx) => {
              const key = row.domain.toLowerCase().replace(/\.$/, "");

              this.$set(this.domainListData[idx], "companies", merged[key] || []);
            });
            this.domainListData = this.groupRowsByCompanies(this.domainListData);
            this.currentPage = 1;
          }).catch(() => {
            this.domainListData.forEach((_, idx) => {
              this.$set(this.domainListData[idx], "companies", []);
            });
          });
        }
      } catch (error) {
        console.error("查询失败", error);
        this.$message.error("查询失败，请稍后重试");
      } finally {
        clearInterval(this.loadingTimer);
        this.loadingTimer = null;
        this.isLoading = false;
      }
    },

    // 处理表格行点击
    async handleRowClick(row) {
      this.selectedDomain = row.domain;
      this.domainDetail = null;
      this.isLoading = true;
      this.loadingText = "Loading domain details...";

      try {
        const res = await getDomainAuthRiskDetail({ 域名: row.domain });

        if (res.code === 200) {
          this.domainDetail = res.data;
          this.dialogVisible = true;
        }
      } catch (error) {
        console.error("获取域名详情失败", error);
        this.$message.error("获取域名详情失败");
      } finally {
        this.isLoading = false;
      }
    },

    // 表格行样式
    tableRowClassName() {
      return "clickable-row";
    },

    // 获取风险等级样式类
    getRiskClass(riskLevel) {
      const classMap = {
        "域名劫持事件": "risk-level-hijack",
        "严重配置风险警告": "risk-level-severe",
        "重要配置风险告警": "risk-level-important",
        "一般配置风险提示": "risk-level-general",
        "一般配置提示": "risk-level-general"
      };

      return classMap[riskLevel] || "";
    },

    formatRiskLevelLabel(riskLevel) {
      return riskLevel === "一般配置风险提示" ? "一般配置提示" : riskLevel;
    },

    // 把同机构的域名相邻摆放：以 companies 排序后 join 作为分组 key，
    // 按各分组首次出现顺序输出；无机构归属的行集中排到最后
    groupRowsByCompanies(rows) {
      const order = new Map();
      const buckets = new Map();
      const empty = [];

      rows.forEach(row => {
        const list = Array.isArray(row.companies) ? row.companies : [];

        if (!list.length) {
          empty.push(row);
          return;
        }
        const key = [...list].sort().join("||");

        if (!order.has(key)) {
          order.set(key, order.size);
          buckets.set(key, []);
        }
        buckets.get(key).push(row);
      });

      const ordered = [];

      [...order.keys()].forEach(key => {
        ordered.push(...buckets.get(key));
      });
      return ordered.concat(empty);
    },

    // 分页切换
    handlePageChange(page) {
      this.currentPage = page;
    },

    // 导航切换
    handleNavChange(value) {
      if (value === "authrisk") {
        this.$router.push("/authrisk");
      } else if (value === "recrisk") {
        this.$router.push("/recrisk");
      }
    }
  }
};
</script>

<style lang="less" scoped>
.entity-link-container {
  width: 100%;
  min-height: var(--screen-height, 100vh);
  height: var(--screen-height, 100vh);
  background: url("../assets/background.png") no-repeat center center #000;
  background-size: cover;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
}

.leftSider, .rightSider {
  position: fixed;
  z-index: 1;
  display: none; // 在小屏幕隐藏装饰

  @media (min-width: 1366px) {
    display: block;
  }

  img {
    height: var(--screen-height, 100vh);
    width: auto;
  }
}

.leftSider {
  left: 0;
  top: 0;
}

.rightSider {
  right: 0;
  top: 0;
}

// 搜索区域
.search-section {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 700px;
  text-align: center;
  transition: all 0.5s ease;
  z-index: 10;
  padding: 0 20px;

  &.searched {
    position: relative;
    top: 50px;
    left: 0;
    transform: none;
    width: 100%;
    max-width: none;
    padding: 0 4%;
    margin-bottom: 10px;

    @media (min-width: 768px) {
      padding: 0 6%;
      top: 60px;
    }

    @media (min-width: 1366px) {
      top: 70px;
      padding: 0 80px;
    }
  }
}

.search-box {
  .search-input {
    /deep/ .el-input__inner {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: #fff;
      height: 45px;
      font-size: clamp(14px, 2vw, 16px);

      @media (max-width: 768px) {
        height: 40px;
      }

      &:focus {
        border-color: #1890FF;
      }
    }

    /deep/ .el-input-group__append {
      background: #1890FF;
      border-color: #1890FF;
      color: #fff;
      cursor: pointer;

      &:hover {
        background: #40a9ff;
      }

      .el-button {
        color: #fff;
        font-size: clamp(14px, 1.8vw, 16px);
      }
    }
  }
}

.entity-count-tip {
  margin-top: clamp(20px, 3vw, 30px);
  font-size: clamp(14px, 1.8vw, 16px);
  color: rgba(255, 255, 255, 0.65);

  .count-number {
    color: #1890FF;
    font-size: clamp(20px, 2.5vw, 24px);
    font-weight: bold;
    margin: 0 5px;
  }
}

// 结果区域
.result-section {
  position: relative;
  top: 90px;
  padding: 0 4% 150px;
  z-index: 10;

  @media (min-width: 768px) {
    top: 100px;
    padding: 0 6% 150px;
  }

  @media (min-width: 1366px) {
    top: 110px;
    padding: 0 80px 150px;
  }
}

.entity-info-card,
.domain-list-card,
.risk-stats-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: clamp(12px, 1.8vw, 20px);
  margin-bottom: clamp(8px, 1.5vw, 15px);
  box-sizing: border-box;
  overflow: hidden;
}

// 主区 + 侧边栏布局
.result-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: clamp(8px, 1.5vw, 15px);
  align-items: start;

  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 1fr) minmax(320px, 420px);
  }
}

.result-main {
  min-width: 0;
}

.result-sidebar {
  min-width: 0;

  .risk-stats-card {
    margin-bottom: 0;
  }
}

// 实体信息行：左右横向布局
.entity-info-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: clamp(10px, 1.5vw, 15px);

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}

// 域名列表卡片特殊样式：确保表格有固定高度
.domain-list-card {
  display: flex;
  flex-direction: column;
  min-height: 350px; // 最小高度适配5行数据

  @media (min-width: 768px) {
    min-height: 400px;
  }

  .section-title {
    flex-shrink: 0; // 标题不收缩
    margin-bottom: 12px;
  }

  .domain-list-table {
    flex: 1; // 表格占据剩余空间
    min-height: 0; // 重要：允许表格收缩
    margin-bottom: 12px;
  }

  .pagination {
    flex-shrink: 0; // 分页不收缩
  }
}

.entity-name {
  font-size: clamp(16px, 2.2vw, 20px);
  color: #fff;
  font-weight: bold;
  margin-bottom: 0; // 横向布局时不需要margin
  flex-shrink: 0; // 名称不收缩
}

.domain-summary {
  font-size: clamp(14px, 1.8vw, 16px);
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 0; // 横向布局时不需要margin
  flex: 1; // 摘要可以占据剩余空间

  .highlight {
    color: #1890FF;
    font-weight: bold;
    font-size: clamp(16px, 2vw, 18px);
    margin: 0 5px;
  }
}

.section-title {
  font-size: clamp(16px, 2vw, 18px);
  color: #fff;
  font-weight: bold;
  margin-bottom: 12px;
  padding-left: 10px;
  border-left: 4px solid #1890FF;
}

.risk-stats-section {
  margin-top: clamp(8px, 1.2vw, 12px);
}

.risk-stats-card {
  display: flex;
  flex-direction: column;
  min-height: 0;

  .section-title {
    margin-bottom: 12px;
  }

  .risk-stats-table {
    flex: 1;
    overflow-x: auto;
  }
}

// 表格样式
/deep/ .el-table {
  background: transparent;
  color: #fff;
  width: 100% !important;

  th {
    background: rgba(24, 144, 255, 0.1);
    color: #fff;
    font-weight: bold;
    border-color: rgba(255, 255, 255, 0.1);
  }

  td {
    border-color: rgba(255, 255, 255, 0.1);
  }

  tr {
    background: transparent;
  }

  .clickable-row {
    cursor: pointer;

    &:hover > td {
      background: rgba(24, 144, 255, 0.1) !important;
    }
  }
}

// 表格容器添加滚动
.risk-stats-table {
  overflow-x: auto;
}

.domain-list-table {
  overflow: visible; // 允许表格正常显示

  /deep/ .el-table__body-wrapper {
    overflow-y: auto; // 允许纵向滚动
    overflow-x: auto; // 允许横向滚动

    // 自定义滚动条样式
    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(24, 144, 255, 0.3);
      border-radius: 3px;

      &:hover {
        background: rgba(24, 144, 255, 0.5);
      }
    }
  }
}

// 风险等级颜色
.risk-level-hijack,
.risk-count.hijack {
  color: #F5222D;
  font-weight: bold;
}

.risk-level-severe,
.risk-count.severe {
  color: #FA8C16;
  font-weight: bold;
}

.risk-level-important,
.risk-count.important {
  color: #FAAD14;
  font-weight: bold;
}

.risk-level-general,
.risk-count.general {
  color: #1890FF;
  font-weight: bold;
}

.company-loading {
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
}

.company-none {
  color: rgba(255, 255, 255, 0.3);
}

.company-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.company-tag {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.85);
  max-width: none;
  white-space: normal;
  height: auto;
  line-height: 1.4;
  padding: 2px 6px;
}

// 分页样式
.pagination {
  margin-top: 10px;
  text-align: center;

  /deep/ .el-pagination {
    .el-pager li {
      background: transparent;
      color: #fff;
      border: 1px solid rgba(255, 255, 255, 0.2);

      &.active {
        background: #1890FF;
        border-color: #1890FF;
      }
    }

    button {
      background: transparent;
      color: #fff;
    }
  }
}

// 加载遮罩
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;

  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }

  .loading-dots {
    display: flex;
    gap: 20px;

    span {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #1890FF;
      animation: bounce 1.2s infinite ease-in-out;

      &:nth-child(2) { animation-delay: 0.2s; }
      &:nth-child(3) { animation-delay: 0.4s; }
    }
  }

  .loading-text {
    font-size: 20px;
    color: #1890FF;
    letter-spacing: 2px;
    font-family: monospace;
    min-width: 300px;
    text-align: center;
    animation: fadeSwitch 0.4s ease;
  }
}

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1.2); opacity: 1; }
}

@keyframes fadeSwitch {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

// 弹窗样式
/deep/ .risk-detail-dialog {
  .el-dialog {
    background: rgba(10, 22, 40, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
    width: 90% !important;
    max-width: 1000px;
    margin-top: 5vh !important;
  }

  .el-dialog__title {
    color: #fff;
    font-size: clamp(16px, 2vw, 20px);
  }

  .el-dialog__headerbtn .el-dialog__close {
    color: #fff;
  }

  .el-dialog__body {
    max-height: var(--screen-dialog-max-height, 70vh);
    overflow-y: auto;
    padding: clamp(15px, 2vw, 20px);
  }
}

.risk-detail-content {
  .risk-category {
    margin-bottom: clamp(15px, 2vw, 25px);

    .category-title {
      font-size: clamp(16px, 2vw, 18px);
      font-weight: bold;
      padding: 10px 15px;
      border-radius: 4px;
      margin-bottom: 15px;

      &.severe {
        background: rgba(250, 140, 22, 0.1);
        color: #FA8C16;
        border-left: 4px solid #FA8C16;
      }

      &.important {
        background: rgba(250, 173, 20, 0.1);
        color: #FAAD14;
        border-left: 4px solid #FAAD14;
      }

      &.general {
        background: rgba(24, 144, 255, 0.1);
        color: #1890FF;
        border-left: 4px solid #1890FF;
      }

      &.hijack {
        background: rgba(245, 34, 45, 0.1);
        color: #F5222D;
        border-left: 4px solid #F5222D;
      }
    }

    .risk-item,
    .hijack-item {
      background: rgba(255, 255, 255, 0.03);
      padding: clamp(12px, 1.5vw, 15px);
      border-radius: 4px;
      margin-bottom: 10px;
      border: 1px solid rgba(255, 255, 255, 0.1);

      .risk-type {
        font-size: clamp(14px, 1.8vw, 16px);
        color: #fff;
        font-weight: bold;
        margin-bottom: 8px;
      }

      .risk-location {
        font-size: clamp(12px, 1.6vw, 14px);
        color: rgba(255, 255, 255, 0.65);
        margin-bottom: 8px;
      }

      .risk-explanation {
        font-size: clamp(12px, 1.6vw, 14px);
        color: rgba(255, 255, 255, 0.85);
        line-height: 1.6;
      }

      .hijack-time {
        font-size: clamp(12px, 1.6vw, 14px);
        color: rgba(255, 255, 255, 0.65);
        margin-bottom: 8px;
      }

      .hijack-ips {
        font-size: clamp(12px, 1.6vw, 14px);
        color: #fff;
        margin-bottom: 10px;

        .ip-label {
          color: rgba(255, 255, 255, 0.65);
        }
      }

      .hijack-analysis {
        font-size: clamp(12px, 1.6vw, 14px);
        color: rgba(255, 255, 255, 0.85);
        line-height: 1.8;

        strong {
          color: #fff;
        }
      }
    }
  }
}
.beian-footer {
  position: relative;
  z-index: 30;
  margin-top: 32px;
  padding-bottom: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.58);
  font-size: 13px;
}

.beian-footer a {
  color: rgba(255, 255, 255, 0.68);
  text-decoration: none;
}

.beian-footer a:hover {
  color: #00e5ff;
}

.beian-police-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.beian-police-logo {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

</style>
